import {
  RAPPORTTYPER,
  type Omradetilstand,
  type RapportoppgaverDto,
  type Rapporttype,
} from '@es/shared';
import { formaterFrist } from '../prognose/leveringer.js';

/**
 * Arbeidsflyten i rapporten, slik siden sier den.
 *
 * Rene funksjoner: hvor langt et område har kommet, og om det er forsinket;
 * hvordan et forslag legges inn i teksten; og lenkene fra oppgavene på
 * forsiden rett til riktig rapport.
 */

export const TILSTANDSNAVN: Record<Omradetilstand, string> = {
  ikke_startet: 'Ikke påbegynt',
  i_arbeid: 'I arbeid',
  ferdig: 'Ferdig',
};

/**
 * Tilstanden i ord, og om området er forsinket: fristen er passert, og det er
 * ikke markert som ferdig. Fristen er siste dag — på selve dagen er ingen sent ute.
 *
 * @param idag Dagens dato i norsk tid, se `datoIOslo`.
 */
export function omradestatus(
  tilstand: Omradetilstand,
  frist: string | null,
  idag: string,
): { tekst: string; forsinket: boolean } {
  const forsinket = tilstand !== 'ferdig' && frist !== null && idag > frist;
  return {
    tekst: forsinket ? `${TILSTANDSNAVN[tilstand]} · forsinket` : TILSTANDSNAVN[tilstand],
    forsinket,
  };
}

/**
 * Fristen i ord: «Frist 5. oktober.», eller «Fristen var …» når den er passert.
 * `gjelder` settes inn etter «Frist», som « for kapitlene».
 */
export function fristtekst(frist: string, idag: string, gjelder = ''): string {
  const ar = Number(idag.slice(0, 4));
  return `${idag > frist ? `Fristen${gjelder} var` : `Frist${gjelder}`} ${formaterFrist(frist, ar)}.`;
}

/** Forslaget lagt til på slutten av teksten, som et eget avsnitt. */
export function leggTilForslag(utkast: string, forslag: string): string {
  const for_ = utkast.trimEnd();
  return for_ === '' ? forslag.trim() : `${for_}\n\n${forslag.trim()}`;
}

type Rapportadresse = Pick<RapportoppgaverDto['oppgaver'][number], 'ar' | 'rapporttype' | 'nummer'>;

/** Lenken fra en oppgave til rapporten den gjelder. */
export function rapportlenke(o: Rapportadresse): string {
  const sporring = new URLSearchParams({ ar: String(o.ar), type: o.rapporttype });
  if (o.nummer !== null) sporring.set('nummer', String(o.nummer));
  return `/rapportering?${sporring.toString()}`;
}

/**
 * Rapporten lenken peker på, eller null hvis den ikke peker på noen gyldig.
 * Året må være et siden tilbyr; nummeret må passe til typen.
 */
export function lesRapportlenke(
  sporring: URLSearchParams,
  ar: readonly number[],
): { type: Rapporttype; ar: number; nummer: number | null } | null {
  const type = RAPPORTTYPER.find((t) => t === sporring.get('type'));
  const aret = ar.find((a) => a === Number(sporring.get('ar')));
  if (type === undefined || aret === undefined) return null;
  if (type === 'ar' || type === 'budsjett') return { type, ar: aret, nummer: null };
  const nummer = Number(sporring.get('nummer'));
  const hoyeste = type === 'maned' ? 12 : 3;
  return Number.isInteger(nummer) && nummer >= 1 && nummer <= hoyeste
    ? { type, ar: aret, nummer }
    : null;
}

/** En rapportoppgave i ord: hva som skal gjøres, og hvor langt det er kommet. */
export function rapportoppgavetekst(o: RapportoppgaverDto['oppgaver'][number]): {
  tittel: string;
  detalj: string;
} {
  switch (o.oppgave) {
    case 'skriv': {
      const forslag =
        o.nyeForslag === 0
          ? ''
          : ` ${String(o.nyeForslag)} ${o.nyeForslag === 1 ? 'nytt forslag' : 'nye forslag'} fra avdelingslederne.`;
      return {
        tittel: `Skriv kapitlet for ${o.omradeNavn} — ${o.tittel}`,
        detalj: `${o.tilstand === 'ikke_startet' ? 'Ikke påbegynt.' : 'I arbeid.'}${forslag}`,
      };
    }
    case 'folg_opp':
      return {
        tittel: `Følg opp ${o.tittel}`,
        detalj: `${String(o.ferdige)} av ${String(o.antall)} ${o.antall === 1 ? 'område' : 'områder'} er ferdige.`,
      };
    case 'foresla':
      return {
        tittel: `Foreslå tekst for ${o.omradeNavn} — ${o.tittel}`,
        detalj: 'Kommunalsjefen skriver kapitlet, og kan bruke det du foreslår.',
      };
    case 'les':
      return {
        tittel: `${o.tittel} til behandling i ${o.utvalgNavn}`,
        detalj: 'Les rapporten, og kommenter det du vil ha svar på før møtet.',
      };
    case 'svar':
      return {
        tittel: `Svar politikerne — ${o.tittel}`,
        detalj: `${String(o.antall)} ${o.antall === 1 ? 'kommentar' : 'kommentarer'} fra ${o.utvalgNavn} venter på svar.`,
      };
  }
}

/** Lenken til den politiske behandlingen, til listen eller til én utsendelse. */
export function behandlingslenke(utsendelseId: string | null = null): string {
  return `/rapportering?fane=behandling${utsendelseId === null ? '' : `&utsendelse=${utsendelseId}`}`;
}

/** Lenken fra en oppgave: til rapporten, eller til den politiske behandlingen. */
export function oppgavelenke(o: RapportoppgaverDto['oppgaver'][number]): string {
  return o.oppgave === 'les' || o.oppgave === 'svar'
    ? behandlingslenke(o.utsendelseId)
    : rapportlenke(o);
}
